import { FC, useCallback, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { QueryStatus, useQuery, useQueryClient } from 'react-query';
import { RefreshIcon } from '@heroicons/react/outline';
import {
  giftOrderListForAdminDataAtomFamily,
  giftOrderListForAdminStatusAtomFamily,
} from '@shared/api/giftOrder/giftOrder.atom';
import {
  sendGiftVBankAlimUser,
  sendPhoneOrderTestPaidAlert,
  setPhoneOrderDocStatus,
} from '@shared/api/giftOrder/giftOrder.controller';
import {
  GiftOrderDocStatus,
  GiftOrderForAdmin,
  GiftOrderListForAdmin,
  GiftOrderPaymentStatus,
} from '@shared/api/giftOrder/giftOrder.interface';
import { GiftPhoneMemoPad } from '@shared/components/GiftPhoneMemoPad';
import { Loading } from '@shared/components/Loading';
import { queryString, useRecoilQuery } from '@shared/hooks/recoil-query';
import { api_ } from '@shared/plugIn/axios';
import { useAuth } from '@shared/state/hooks/useAuth';
import { LowerRole } from '@shared/types';
import { formatDate } from '@shared/utils/formatDate';
import { formatPrice } from '@shared/utils/formatPrice';
import { useSearchParams } from 'react-router-dom';

import { GiftPhoneCatalogSection } from '../components/GiftPhoneCatalogSection';
import { useSessionPersistedState } from '../hooks/useSessionPersistedState';
import { GiftMemberSearchSection } from './GiftMemberSearchPage';

/**
 * 전화주문 관리 페이지 (Admin·GiftAgent) — 전화주문 흐름 전체를 한 화면에서.
 *
 * 탭 구조 (?tab= 로 전환, 북마크 보존):
 * - 고객검색: 상주 검색 → 주문 이력 확인·대리주문 진입 (GiftMemberSearchSection)
 * - 입금 확인: 전화주문(viaPhoneOrder 마커) 목록. 입금되면 Bootpay 웹훅이
 *   자동으로 Paid 처리하므로 새로고침 조회만 하면 된다. (수동 입금확인 없음)
 *
 * viaPhoneOrder는 대리주문 생성 시 선배선으로 보내는 마커 — 백엔드 스키마 반영
 * 전에는 저장되지 않아 목록이 빈다 (docs/gift-member-search-spec.md).
 * 조회는 기존 제네릭 GET /admin/gift-order에 필터만 얹는다 (백엔드 무수정).
 */

const FILTER_PREFIX = 'phoneOrder';

/**
 * 전화주문 전용 조회 — 백엔드가 필터·populate를 고정한 전용 라우트
 * (GiftAgent·Admin 공용, viaPhoneOrder 부분 인덱스 사용).
 */
const phoneOrderListUrl = (from: string, to: string) =>
  `/gift-agent/gift-order/phone-order?from=${from}&to=${to}`;

/**
 * 구서버(전용 라우트 미배포) Admin 폴백 — 제네릭 + $expr.
 * ⚠️ 필터에 `viaPhoneOrder: true`로 쓰면 안 된다 — 백엔드 mongoose가
 * strictQuery:true라 스키마에 없는 필드 조건을 조용히 버려 전체 주문이
 * 매칭된다(실사고). $expr는 연산자라 살아남고 MongoDB가 직접 평가한다.
 */
const legacyDepositListUrl = (from: string, to: string) => {
  const params: Record<string, unknown> = {
    filter: {
      $expr: { $eq: ['$viaPhoneOrder', true] },
      // 결제창 이탈로 지워진 주문 제외 (Active 양성 매칭 금지 — 구 문서 대비)
      docStatus: { $ne: GiftOrderDocStatus.Deleted },
    },
    populate: [{ path: 'fevent', select: 'deceasedInfo' }],
    options: { sort: { createdAt: -1 }, limit: 300 },
    from,
    to,
  };
  return `/${LowerRole.Admin}/gift-order?${queryString(params)}`;
};

const pad2 = (n: number) => String(n).padStart(2, '0');

// 한국시간 기준 n일 전 날짜 (0=오늘, 1=어제) — 기기 시간대와 무관하게 KST로 계산
const kstDaysAgoYmd = (daysAgo: number) => {
  const kst = new Date(Date.now() + 9 * 3600 * 1000);
  kst.setUTCDate(kst.getUTCDate() - daysAgo);
  return `${kst.getUTCFullYear()}-${pad2(kst.getUTCMonth() + 1)}-${pad2(
    kst.getUTCDate(),
  )}`;
};

// 기간 프리셋 — 모두 오늘 마감. 기본은 어제·오늘 2일.
const DATE_PRESETS = [
  { label: '어제·오늘', fromDaysAgo: 1 },
  { label: '3일', fromDaysAgo: 2 },
  { label: '7일', fromDaysAgo: 6 },
];

type StatusFilter = 'all' | 'pending' | 'paid' | 'canceled' | 'deleted';

const isPending = (status: GiftOrderPaymentStatus) =>
  status === GiftOrderPaymentStatus.NotPaid || status === GiftOrderPaymentStatus.Waiting;

const isPaidLike = (status: GiftOrderPaymentStatus) =>
  status === GiftOrderPaymentStatus.Paid || status === GiftOrderPaymentStatus.Confirmed;

// 가상계좌 유효기간 — 발급 시 deposit_expiration을 24시간으로 요청한다
// (getGiftBootpayRequestDto.ts). 지난 미입금 건은 입금 불가로 보고 표시한다.
const VBANK_EXPIRE_HOURS = 24;

const isVbankExpired = (order: GiftOrderForAdmin) =>
  isPending(order.paymentDetail.status) &&
  Date.now() - new Date(order.createdAt).getTime() > VBANK_EXPIRE_HOURS * 3600 * 1000;

const StatusChip: FC<{ order: GiftOrderForAdmin }> = function StatusChip({ order }) {
  const status = order.paymentDetail.status;
  if (isPending(status)) {
    return (
      <span className="inline-flex flex-col items-start gap-0.5">
        <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
          입금대기
        </span>
        {isVbankExpired(order) && (
          <span className="text-xs text-red-500">발급 후 24시간 지남</span>
        )}
      </span>
    );
  }
  if (isPaidLike(status)) {
    return (
      <span className="rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
        {status === GiftOrderPaymentStatus.Confirmed ? '구매확정' : '입금완료'}
      </span>
    );
  }
  if (status === GiftOrderPaymentStatus.CancelRequested) {
    return (
      <span className="rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
        취소요청
      </span>
    );
  }
  return (
    <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
      취소됨
    </span>
  );
};

const formatDateTime = (date: Date) =>
  formatDate(date, { dateSeparater: '.', contains: { hours: true, minutes: true } });

const orderItemSummary = (order: GiftOrderForAdmin): string => {
  const first = order.orderDetails[0];
  if (!first) return '-';
  const name = first.giftItemEmbed?.name ?? '답례품';
  const rest = order.orderDetails.length - 1;
  return rest > 0 ? `${name} 외 ${rest}건` : `${name} x ${first.quantity}`;
};

// 삭제(되살릴 수 있는 목록에서 감추기) 가능한 상태 — 입금완료 건은 매출·정산에
// 걸리므로 삭제하지 않는다(취소 처리가 맞는 경로).
const isDeletable = (order: GiftOrderForAdmin) =>
  isPending(order.paymentDetail.status) ||
  order.paymentDetail.status === GiftOrderPaymentStatus.Canceled;

/**
 * 결제완료 알림 테스트 발송 모달.
 *
 * 가상계좌에 실제로 입금하면 수수료가 환불되지 않아, 입금 없이 "결제완료" 문자·
 * 알림톡이 고객에게 어떻게 도착하는지만 확인할 수 있게 한다.
 * 주문 상태는 바뀌지 않으며, 받는 번호는 상주 번호가 아니라 담당자 본인 번호를
 * 기본값으로 넣는다(실제 고객에게 잘못 가는 것 방지).
 */
const TestPaidAlertModal: FC<{
  order: GiftOrderForAdmin;
  onClose: () => void;
}> = function TestPaidAlertModal({ order, onClose }) {
  const { userProfile } = useAuth();
  const [phoneNumber, setPhoneNumber] = useState(
    (userProfile?.info?.phoneNumber ?? '').replace(/[^0-9]/g, ''),
  );
  const [isSending, setIsSending] = useState(false);

  const handleSend = useCallback(async () => {
    const digits = phoneNumber.replace(/[^0-9]/g, '');
    if (digits.length < 10) {
      alert('받는 번호를 정확히 입력해주세요.');
      return;
    }
    const customerDigits = (order.receiverPhoneNumber ?? '').replace(/[^0-9]/g, '');
    const confirmed = window.confirm(
      `${digits} 번호로 결제완료 알림을 테스트 발송합니다.\n\n` +
        (digits === customerDigits
          ? '⚠️ 이 번호는 주문하신 상주 번호입니다. 실제 고객에게 발송됩니다.\n'
          : '') +
        '주문 상태는 바뀌지 않습니다.',
    );
    if (!confirmed) return;

    setIsSending(true);
    try {
      await sendPhoneOrderTestPaidAlert(order._id, digits);
      alert('테스트 알림을 발송했습니다. 해당 번호에서 확인해주세요.');
      onClose();
    } catch (err) {
      console.error(err);
      const errStatus = (err as { response?: { status?: number } })?.response?.status;
      alert(
        errStatus === 404
          ? '테스트 발송 기능이 아직 서버에 준비되지 않았습니다. 서버 업데이트 후 사용할 수 있습니다.'
          : '발송에 실패했습니다. 잠시 후 다시 시도해주세요.',
      );
    } finally {
      setIsSending(false);
    }
  }, [phoneNumber, order._id, order.receiverPhoneNumber, onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md whitespace-normal rounded-lg bg-white p-5 text-left shadow-xl">
        <h3 className="text-base font-bold text-gray-900">결제완료 알림 테스트</h3>
        <p className="mt-1 text-sm text-gray-500">
          입금하지 않고 결제완료 문자·알림톡이 어떻게 도착하는지 확인합니다. 주문 상태는
          바뀌지 않습니다.
        </p>

        <div className="mt-4 rounded-md bg-gray-50 p-3 text-sm text-gray-700">
          <p>
            <span className="text-gray-500">주문 </span>
            {order.receiverName ?? '-'} · {orderItemSummary(order)} ·{' '}
            {formatPrice(order.paymentDetail.paymentByCash)}원
          </p>
        </div>

        <label className="mt-4 block text-sm font-medium text-gray-700">받는 번호</label>
        <input
          type="tel"
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          placeholder="01012345678"
          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
        />
        <p className="mt-1 text-xs text-gray-500">
          담당자 본인 번호가 기본으로 채워집니다. 상주 번호를 넣으면 실제 고객에게
          발송됩니다.
        </p>

        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            닫기
          </button>
          <button
            onClick={handleSend}
            disabled={isSending}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300"
          >
            {isSending ? '발송 중…' : '테스트 발송'}
          </button>
        </div>
      </div>
    </div>
  );
};

const OrderRow: FC<{
  order: GiftOrderForAdmin;
  isDeletedView?: boolean;
  onDocStatusChanged: () => void;
  onTestAlert: (order: GiftOrderForAdmin) => void;
}> = function OrderRow({
  order,
  isDeletedView = false,
  onDocStatusChanged,
  onTestAlert,
}) {
  const [isResending, setIsResending] = useState(false);
  const [isUpdatingDocStatus, setIsUpdatingDocStatus] = useState(false);

  const vBank = order.paymentDetail.vBank ?? '';
  const [bankName = '', bankAccount = ''] = vBank.split('\n');

  const handleDocStatus = useCallback(
    async (next: GiftOrderDocStatus) => {
      const isDelete = next === GiftOrderDocStatus.Deleted;
      const confirmed = window.confirm(
        isDelete
          ? `${
              order.receiverName ?? ''
            }님 주문을 목록에서 삭제합니다.\n삭제함에서 되살릴 수 있습니다.`
          : `${order.receiverName ?? ''}님 주문을 되살립니다.`,
      );
      if (!confirmed) return;
      setIsUpdatingDocStatus(true);
      try {
        await setPhoneOrderDocStatus(order._id, next);
        onDocStatusChanged();
      } catch (err) {
        console.error(err);
        const errStatus = (err as { response?: { status?: number } })?.response?.status;
        alert(
          errStatus === 404
            ? '삭제 기능이 아직 서버에 준비되지 않았습니다. 서버 업데이트 후 사용할 수 있습니다.'
            : '처리에 실패했습니다. 잠시 후 다시 시도해주세요.',
        );
      } finally {
        setIsUpdatingDocStatus(false);
      }
    },
    [order._id, order.receiverName, onDocStatusChanged],
  );

  const handleCopyVbank = useCallback(() => {
    navigator.clipboard?.writeText(
      `${bankName} ${bankAccount} ${formatPrice(order.paymentDetail.paymentByCash)}원`,
    );
  }, [bankName, bankAccount, order.paymentDetail.paymentByCash]);

  const handleResend = useCallback(async () => {
    if (!vBank) return;
    const confirmed = window.confirm(
      `${order.receiverPhoneNumber ?? ''} 번호로 입금안내 문자를 다시 보냅니다.`,
    );
    if (!confirmed) return;
    setIsResending(true);
    try {
      await sendGiftVBankAlimUser(order._id, vBank);
      alert('입금안내 문자를 다시 보냈습니다.');
    } catch (err) {
      console.error(err);
      alert('문자 발송에 실패했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setIsResending(false);
    }
  }, [order._id, order.receiverPhoneNumber, vBank]);

  return (
    <tr className="hover:bg-gray-50">
      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
        {formatDateTime(new Date(order.createdAt))}
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-sm">
        <p className="font-medium text-gray-900">{order.receiverName ?? '-'}</p>
        <p className="text-xs text-gray-500">{order.receiverPhoneNumber ?? '-'}</p>
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
        {order.fevent?.deceasedInfo?.name ?? '-'}
      </td>
      <td className="px-4 py-3 text-sm text-gray-700">{orderItemSummary(order)}</td>
      <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-gray-900">
        {formatPrice(order.paymentDetail.paymentByCash)}원
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
        <div className="flex items-center gap-2">
          <div>
            <p>{bankName || '-'}</p>
            <p className="text-xs text-gray-500">{bankAccount}</p>
          </div>
          {vBank && (
            <button
              onClick={handleCopyVbank}
              title="은행·계좌·금액 복사"
              className="rounded border border-gray-300 px-1.5 py-0.5 text-xs text-gray-500 hover:bg-gray-100"
            >
              복사
            </button>
          )}
        </div>
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-sm">
        <StatusChip order={order} />
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-right text-sm">
        <div className="flex justify-end gap-2">
          {isDeletedView ? (
            <button
              onClick={() => handleDocStatus(GiftOrderDocStatus.Active)}
              disabled={isUpdatingDocStatus}
              className="rounded-md border border-indigo-300 bg-white px-2.5 py-1 text-xs font-medium text-indigo-600 hover:bg-indigo-50 disabled:opacity-50"
            >
              {isUpdatingDocStatus ? '처리 중…' : '되살리기'}
            </button>
          ) : (
            <>
              {isPending(order.paymentDetail.status) && vBank && (
                <button
                  onClick={handleResend}
                  disabled={isResending}
                  className="rounded-md border border-indigo-300 bg-white px-2.5 py-1 text-xs font-medium text-indigo-600 hover:bg-indigo-50 disabled:opacity-50"
                >
                  {isResending ? '발송 중…' : '안내 재발송'}
                </button>
              )}
              {order.paymentDetail.receiptUrl && (
                <a
                  href={order.paymentDetail.receiptUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
                >
                  영수증
                </a>
              )}
              {isPending(order.paymentDetail.status) && (
                <button
                  onClick={() => onTestAlert(order)}
                  title="입금 없이 결제완료 알림이 어떻게 도착하는지 확인"
                  className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
                >
                  알림 테스트
                </button>
              )}
              {isDeletable(order) && (
                <button
                  onClick={() => handleDocStatus(GiftOrderDocStatus.Deleted)}
                  disabled={isUpdatingDocStatus}
                  title="목록에서 삭제 (삭제함에서 되살릴 수 있음)"
                  className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                >
                  {isUpdatingDocStatus ? '처리 중…' : '삭제'}
                </button>
              )}
            </>
          )}
        </div>
      </td>
    </tr>
  );
};

//* 주문일시가 선택 기간(한국시간 하루 경계) 안인지
const isCreatedWithinKst = (order: GiftOrderForAdmin, from: string, to: string) => {
  const startMs = from ? new Date(`${from}T00:00:00+09:00`).getTime() : -Infinity;
  const endMs = to ? new Date(`${to}T23:59:59.999+09:00`).getTime() : Infinity;
  const createdMs = new Date(order.createdAt).getTime();
  return createdMs >= startMs && createdMs <= endMs;
};

/**
 * 전화주문 목록 조회 — 탭 배지(입금대기 건수)와 입금 확인 탭이 같은 데이터를 쓰도록
 * 페이지에서 한 번만 조회한다. (탭을 열지 않아도 배지가 보여야 하므로 페이지 레벨)
 */
const usePhoneOrders = (from: string) => {
  const { isAdmin } = useAuth();

  // 서버 조회 범위는 항상 오늘까지(선택 종료일과 무관) + 넉넉한 과거(90일 또는
  // 선택 시작일 중 이른 쪽).
  // 입금대기는 실시간 큐라 기간 선택과 무관하게 항상 보여야 하므로(짧은 기간을
  // 골라도 예전 미입금 건이 사라지면 안 됨), 기간 좁히기는 클라이언트에서 한다.
  const serverFrom = from && from < kstDaysAgoYmd(90) ? from : kstDaysAgoYmd(90);
  const serverTo = kstDaysAgoYmd(0);
  const listUrl = phoneOrderListUrl(serverFrom, serverTo);
  const filterKey = `${FILTER_PREFIX}_${serverFrom}_${serverTo}`;

  // 전용 라우트 우선, 미배포 서버(404)면 Admin 한정 제네릭+$expr 폴백
  const fetchPhoneOrders = useCallback(async () => {
    try {
      const { data } = await api_.get<GiftOrderListForAdmin>(listUrl);
      return data;
    } catch (err) {
      const errStatus = (err as { response?: { status?: number } })?.response?.status;
      if (errStatus === 404 && isAdmin()) {
        const { data } = await api_.get<GiftOrderListForAdmin>(
          legacyDepositListUrl(serverFrom, serverTo),
        );
        return data;
      }
      throw err;
    }
  }, [listUrl, serverFrom, serverTo, isAdmin]);

  useRecoilQuery(
    giftOrderListForAdminStatusAtomFamily(filterKey),
    giftOrderListForAdminDataAtomFamily(filterKey),
    listUrl,
    fetchPhoneOrders,
    false,
  );
  const status = useRecoilValue(giftOrderListForAdminStatusAtomFamily(filterKey));
  const orderList = useRecoilValue(giftOrderListForAdminDataAtomFamily(filterKey));

  // 새로고침 — 입금 여부는 Bootpay 웹훅이 서버에 자동 반영하므로 재조회만 하면 된다.
  // ⚠️ recoil status를 'idle'로 바꾸는 방식 금지: react-query는 그걸 모르므로
  // 재요청이 없고, 창 포커스 등 다른 계기가 올 때까지 로딩만 무한정 이어진다(실사고).
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const refresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await queryClient.refetchQueries(listUrl, { active: true });
    } finally {
      setIsRefreshing(false);
    }
  }, [queryClient, listUrl]);

  // 서버 필터의 이중 안전장치 — 서버가 어떤 이유로든 전체를 내려줘도
  // 화면에는 전화주문 마커가 있는 주문만 올린다
  const phoneOrders = useMemo(
    () => (orderList ?? []).filter((order) => order.viaPhoneOrder === true),
    [orderList],
  );

  return { status, phoneOrders, refresh, isRefreshing };
};

interface DepositMonitorSectionProps {
  from: string;
  to: string;
  setFrom: (value: string) => void;
  setTo: (value: string) => void;
  status: QueryStatus;
  phoneOrders: GiftOrderForAdmin[];
  onRefresh: () => void;
  isRefreshing: boolean;
}

//* 입금 확인 탭 — 전화주문 목록/요약
const DepositMonitorSection: FC<DepositMonitorSectionProps> =
  function DepositMonitorSection({
    from,
    to,
    setFrom,
    setTo,
    status,
    phoneOrders,
    onRefresh,
    isRefreshing,
  }) {
    const [statusFilter, setStatusFilter] = useSessionPersistedState<StatusFilter>(
      'giftPhoneOrder.status',
      'all',
    );
    const isDeletedView = statusFilter === 'deleted';

    // 모달은 표(td) 밖에서 렌더한다 — td의 text-right·whitespace-nowrap을
    // 상속받아 레이아웃이 깨지는 문제가 있었다.
    const [testAlertOrder, setTestAlertOrder] = useState<GiftOrderForAdmin | null>(null);

    // 삭제함은 별도 조회 — 평소엔 요청하지 않는다(칩을 눌렀을 때만)
    const deletedUrl = `${phoneOrderListUrl(from, to)}&deleted=1`;
    const {
      data: deletedOrders,
      status: deletedStatus,
      refetch: refetchDeleted,
    } = useQuery(
      deletedUrl,
      async () => (await api_.get<GiftOrderListForAdmin>(deletedUrl)).data,
      { enabled: isDeletedView },
    );

    const handleRefresh = useCallback(() => {
      onRefresh();
      if (isDeletedView) refetchDeleted();
    }, [onRefresh, isDeletedView, refetchDeleted]);

    // 삭제·되살리기 후 두 목록 모두 갱신 (삭제함 개수·본 목록이 함께 바뀜)
    const handleDocStatusChanged = useCallback(() => {
      onRefresh();
      refetchDeleted();
    }, [onRefresh, refetchDeleted]);

    // 삭제함 뷰는 자체 조회 상태를 쓴다
    const listStatus = isDeletedView ? deletedStatus : status;

    // 선택 기간 — 입금완료/취소 이력에만 적용.
    // 입금대기는 실시간 큐라 기간과 무관하게 항상 표시한다.
    const isInSelectedRange = useCallback(
      (order: GiftOrderForAdmin) => isCreatedWithinKst(order, from, to),
      [from, to],
    );

    const filteredOrders = useMemo(() => {
      if (isDeletedView) return deletedOrders ?? [];
      if (statusFilter === 'pending')
        return phoneOrders.filter((order) => isPending(order.paymentDetail.status));
      if (statusFilter === 'paid')
        return phoneOrders.filter(
          (order) => isPaidLike(order.paymentDetail.status) && isInSelectedRange(order),
        );
      if (statusFilter === 'canceled')
        return phoneOrders.filter(
          (order) =>
            (order.paymentDetail.status === GiftOrderPaymentStatus.CancelRequested ||
              order.paymentDetail.status === GiftOrderPaymentStatus.Canceled) &&
            isInSelectedRange(order),
        );
      // 전체 = 입금대기(항상) + 기간 내 나머지
      return phoneOrders.filter(
        (order) => isPending(order.paymentDetail.status) || isInSelectedRange(order),
      );
    }, [phoneOrders, statusFilter, isInSelectedRange, isDeletedView, deletedOrders]);

    const summary = useMemo(() => {
      const pending = phoneOrders.filter((order) =>
        isPending(order.paymentDetail.status),
      );
      const paid = phoneOrders.filter(
        (order) => isPaidLike(order.paymentDetail.status) && isInSelectedRange(order),
      );
      const sum = (orders: GiftOrderForAdmin[]) =>
        orders.reduce((acc, order) => acc + order.paymentDetail.paymentByCash, 0);
      return {
        pendingCount: pending.length,
        pendingAmount: sum(pending),
        paidCount: paid.length,
        paidAmount: sum(paid),
      };
    }, [phoneOrders, isInSelectedRange]);

    const statusChips: { key: StatusFilter; label: string }[] = [
      { key: 'all', label: '전체' },
      { key: 'pending', label: '입금대기' },
      { key: 'paid', label: '입금완료' },
      { key: 'canceled', label: '취소' },
      { key: 'deleted', label: '삭제함' },
    ];

    return (
      <div>
        {/* 필터 바 */}
        <div className="mb-4 flex flex-wrap items-end gap-3 rounded-lg bg-white p-4 shadow">
          <div>
            <label className="block text-xs font-medium text-gray-500">시작일</label>
            <input
              type="date"
              value={from}
              max={to || kstDaysAgoYmd(0)}
              onChange={(e) => setFrom(e.target.value)}
              className="mt-1 rounded-md border border-gray-300 px-2 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500">종료일</label>
            <input
              type="date"
              value={to}
              min={from || undefined}
              max={kstDaysAgoYmd(0)}
              onChange={(e) => setTo(e.target.value)}
              className="mt-1 rounded-md border border-gray-300 px-2 py-1.5 text-sm"
            />
          </div>
          <div className="flex gap-1">
            {DATE_PRESETS.map((preset) => (
              <button
                key={preset.label}
                onClick={() => {
                  setFrom(kstDaysAgoYmd(preset.fromDaysAgo));
                  setTo(kstDaysAgoYmd(0));
                }}
                className="rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs text-gray-600 hover:bg-gray-50"
              >
                {preset.label}
              </button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <div className="flex gap-1">
              {statusChips.map((chip) => (
                <button
                  key={chip.key}
                  onClick={() => setStatusFilter(chip.key)}
                  className={
                    statusFilter === chip.key
                      ? 'rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white'
                      : 'rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50'
                  }
                >
                  {chip.label}
                </button>
              ))}
            </div>
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshIcon
                className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`}
              />
              {isRefreshing ? '조회 중…' : '새로고침'}
            </button>
          </div>
        </div>

        {/* 요약 */}
        <div className="mb-4 grid grid-cols-2 gap-3 md:w-1/2">
          <div className="rounded-lg bg-white p-4 shadow">
            <p className="text-xs text-gray-500">입금대기 (기간과 관계없이 모두 표시)</p>
            <p className="mt-1 text-lg font-bold text-amber-600">
              {summary.pendingCount}건 · {formatPrice(summary.pendingAmount)}원
            </p>
          </div>
          <div className="rounded-lg bg-white p-4 shadow">
            <p className="text-xs text-gray-500">입금완료 (선택한 기간)</p>
            <p className="mt-1 text-lg font-bold text-green-600">
              {summary.paidCount}건 · {formatPrice(summary.paidAmount)}원
            </p>
          </div>
        </div>

        {/* 목록 */}
        {listStatus === 'loading' || listStatus === 'idle' ? (
          <div className="center-box py-16">
            <Loading />
          </div>
        ) : listStatus === 'error' ? (
          <div className="rounded-lg bg-white p-8 text-center text-sm text-red-600 shadow">
            {isDeletedView
              ? '삭제함을 불러오지 못했습니다. 서버 업데이트 전에는 사용할 수 없습니다.'
              : '목록을 불러오지 못했습니다. 잠시 후 다시 시도해주세요.'}
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="rounded-lg bg-white p-8 text-center text-sm text-gray-500 shadow">
            {isDeletedView
              ? '해당 기간에 삭제한 전화주문이 없습니다.'
              : '해당 기간에 전화주문이 없습니다.'}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg bg-white shadow">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {[
                    '주문일시',
                    '주문자',
                    '고인',
                    '상품',
                    '금액',
                    '입금계좌',
                    '상태',
                    '',
                  ].map((header, idx) => (
                    <th
                      key={idx}
                      className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredOrders.map((order) => (
                  <OrderRow
                    key={order._id}
                    order={order}
                    isDeletedView={isDeletedView}
                    onDocStatusChanged={handleDocStatusChanged}
                    onTestAlert={setTestAlertOrder}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {testAlertOrder && (
          <TestPaidAlertModal
            order={testAlertOrder}
            onClose={() => setTestAlertOrder(null)}
          />
        )}
      </div>
    );
  };

type PhoneOrderTab = 'create' | 'deposit' | 'catalog';

export const GiftPhoneOrderPage: FC = function GiftPhoneOrderPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const tab: PhoneOrderTab =
    rawTab === 'deposit' || rawTab === 'catalog' ? rawTab : 'create';

  const handleTabChange = useCallback(
    (next: PhoneOrderTab) => {
      setSearchParams(next === 'create' ? {} : { tab: next }, { replace: true });
    },
    [setSearchParams],
  );

  // 기간 — 새로고침·페이지 왕복에도 유지. 기본값은 한국시간 어제·오늘 2일.
  // (키에 .v3 — 구버전 저장분이 기본값 교체를 덮어쓰는 것 방지)
  const [from, setFrom] = useSessionPersistedState(
    'giftPhoneOrder.from.v3',
    kstDaysAgoYmd(1),
  );
  const [to, setTo] = useSessionPersistedState('giftPhoneOrder.to.v3', kstDaysAgoYmd(0));

  // 조회는 페이지에서 1회 — 탭을 열지 않아도 배지가 보이도록
  const { status, phoneOrders, refresh, isRefreshing } = usePhoneOrders(from);

  // 탭 배지 — 어제·오늘 접수분 중 입금대기 건수 (기간 선택과 무관하게 고정 기준)
  const pendingBadgeCount = useMemo(() => {
    const badgeFrom = kstDaysAgoYmd(1);
    const badgeTo = kstDaysAgoYmd(0);
    return phoneOrders.filter(
      (order) =>
        isPending(order.paymentDetail.status) &&
        isCreatedWithinKst(order, badgeFrom, badgeTo),
    ).length;
  }, [phoneOrders]);

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      {/* 헤더 */}
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900">전화주문 관리</h1>
      </div>

      {/* 탭 */}
      <div className="mb-6 flex gap-1 border-b border-gray-200">
        {(
          [
            { key: 'create', label: '고객검색' },
            { key: 'deposit', label: '입금 확인' },
            { key: 'catalog', label: '상품 목록' },
          ] as { key: PhoneOrderTab; label: string }[]
        ).map((item) => (
          <button
            key={item.key}
            onClick={() => handleTabChange(item.key)}
            className={
              tab === item.key
                ? 'border-b-2 border-indigo-600 px-4 py-2 text-sm font-semibold text-indigo-600'
                : 'border-b-2 border-transparent px-4 py-2 text-sm text-gray-500 hover:text-gray-700'
            }
          >
            {item.label}
            {item.key === 'deposit' && pendingBadgeCount > 0 && (
              <span className="ml-1 rounded-full bg-amber-500 px-1.5 py-0.5 text-xs font-semibold text-white">
                {pendingBadgeCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === 'create' ? (
        <div>
          <p className="mb-4 text-sm text-gray-600">
            상주를 검색해 주문을 만들면 상주 번호로 입금안내 문자가 발송됩니다. 입금
            여부는 입금 확인 탭에서 확인하세요.
          </p>
          <GiftMemberSearchSection />
        </div>
      ) : tab === 'deposit' ? (
        <DepositMonitorSection
          from={from}
          to={to}
          setFrom={setFrom}
          setTo={setTo}
          status={status}
          phoneOrders={phoneOrders}
          onRefresh={refresh}
          isRefreshing={isRefreshing}
        />
      ) : (
        <GiftPhoneCatalogSection />
      )}

      {/* 통화 내용 받아적는 메모 (탭과 무관하게 항상) */}
      <GiftPhoneMemoPad />
    </div>
  );
};
